import { ICourse } from '@/types/course'
import { GlobeIcon, GraduationIcon, LayersIcon } from '../../shared/icons'
import BlankCalendarIcon from '@/app/icons/BlankCalendarIcon/BlankCalendarIcon'
import WebcamIcon from '@/app/icons/WebcamIcon/WebcamIcon'
import { CourseFeature } from './CourseFeature'
import React from 'react'
import { getLangNameFromCode } from 'language-name-map'
import { useTranslations } from 'next-intl'

type CourseFeaturesProps = {
  courseInfo: Pick<ICourse, 'cohorts' | 'language' | 'hasCertificate'>
} & React.HTMLAttributes<HTMLUListElement>

export function CourseFeatures({ courseInfo, ...props }: CourseFeaturesProps) {
  const t = useTranslations('CoursePage')
  const { cohorts = [], language = '', hasCertificate = false } = courseInfo

  const cohortLocations = Array.from(
    new Set(cohorts.map((cohort) => cohort.location).filter(Boolean))
  )
  const formats = Array.from(
    new Set(cohorts.map((cohort) => cohort.format).filter(Boolean))
  ).map((format) => t(`format_${format}`))
  const classTypes = Array.from(
    new Set(cohorts.map((cohort) => cohort.classType).filter(Boolean))
  ).map((classType) => t(`classType_${classType}`))

  const getClassTypesTitle = () => {
    return t('class_types_title_template', {
      classTypes: classTypes.join(` ${t('and')} `),
    })
  }

  return (
    <ul
      {...props}
      className={`grid grid-cols-1 gap-2 md:grid-cols-2 md:gap-x-3 md:gap-y-4 lg:gap-y-4 lg:gap-x-8 md:auto-rows-max ${props.className ?? ''}`}
    >
      {cohortLocations.length > 0 && (
        <li>
          <CourseFeature
            className="h-full"
            title={[
              ...(cohortLocations.length > 1
                ? [`${cohortLocations[0]} ${t('and_other_cities')}`]
                : cohortLocations),
            ].join(' / ')}
            description={t('course_location')}
            icon={<GlobeIcon className="text-mydra-black" />}
          />
        </li>
      )}

      {!!language && (
        <li>
          <CourseFeature
            className="h-full"
            title={t(
              `language_${getLangNameFromCode(language)?.name ?? language}`,
              {}
            )}
            description={t('course_language')}
            icon={<LayersIcon />}
          />
        </li>
      )}

      {!!hasCertificate && (
        <li>
          <CourseFeature
            className="h-full"
            title={t('professional_certification')}
            description={t('upon_course_completion')}
            icon={<GraduationIcon />}
          />
        </li>
      )}

      {formats.length > 0 && (
        <li>
          <CourseFeature
            className="h-full"
            title={formats.join(' / ')}
            description={
              formats.length > 1
                ? t('schedule_flexibility')
                : t('course_format')
            }
            icon={<BlankCalendarIcon />}
          />
        </li>
      )}

      {!!classTypes.length && (
        <li>
          <CourseFeature
            className="h-full"
            title={getClassTypesTitle()}
            description={t('delivered_online')}
            icon={<WebcamIcon />}
          />
        </li>
      )}
    </ul>
  )
}
